import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { EmptyState, FormSkeleton, PageSkeleton, toast } from "@bs/ui";
import { Badge } from "@bs/ui";
import { Button } from "@bs/ui";
import { Input } from "@bs/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bs/ui";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@bs/ui";
import { ConfirmDialog } from "@bs/ui";
import { Field } from "@bs/ui";
import { SimpleSelect } from "@bs/ui";
import { formatMoney } from "@bs/contracts/money";
import { SettingsCard, SettingsPageFrame } from "../../../components/settings/settings-page.tsx";
import { orpc } from "../../../lib/orpc.ts";
import { errorMessage } from "../../../lib/errors.ts";
import { downloadInvoicePdf } from "../../../lib/invoice-download.ts";
import { openCashfreeCheckout } from "../../../lib/cashfree-checkout.ts";

export const Route = createFileRoute("/_store/settings/plan-and-billing")({
  pendingComponent: () => <PageSkeleton />,
  component: PlanAndBillingPage,
});

const TITLE = "Plan & billing";
const DESCRIPTION = "View current subscription, plan quotas, usage, invoices, and submit plan change requests.";

export function PlanAndBillingPage() {
  const queryClient = useQueryClient();
  const query = useQuery(orpc.admin.planAndBilling.get.queryOptions());
  const availablePlansQuery = useQuery(orpc.admin.planAndBilling.availablePlans.queryOptions());
  const { store } = useRouteContext({ from: "/_store" });
  const isOwner = (store?.permissions ?? []).includes("settings.write") && store?.role === "store_owner";

  const requestMutation = useMutation(orpc.admin.planAndBilling.requestChange.mutationOptions());
  const cancelMutation = useMutation(orpc.admin.planAndBilling.cancelRequest.mutationOptions());
  const subscribeMutation = useMutation(orpc.admin.planAndBilling.subscribe.mutationOptions());
  const cancelSubscriptionMutation = useMutation(orpc.admin.planAndBilling.cancelSubscription.mutationOptions());
  const [payPlanCode, setPayPlanCode] = useState<string>("");
  const [payInterval, setPayInterval] = useState<"monthly" | "yearly">("monthly");
  const [payPhone, setPayPhone] = useState("");
  const [confirmCancelSubOpen, setConfirmCancelSubOpen] = useState(false);

  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const compareQuery = useQuery({ ...orpc.admin.planAndBilling.compare.queryOptions(), enabled: compareOpen });
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [selectedInterval, setSelectedInterval] = useState<"monthly" | "yearly">("monthly");
  const [requestNote, setRequestNote] = useState("");
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: orpc.admin.planAndBilling.key() });
  }

  if (query.isLoading) {
    return (
      <SettingsPageFrame title={TITLE} description={DESCRIPTION}>
        <SettingsCard>
          <FormSkeleton />
        </SettingsCard>
      </SettingsPageFrame>
    );
  }

  if (query.isError || !query.data) {
    return (
      <SettingsPageFrame title={TITLE} description={DESCRIPTION}>
        <SettingsCard>
          <EmptyState
            icon={AlertTriangle}
            title="Could not load plan and billing details"
            description={errorMessage(query.error)}
            action={<Button onClick={() => void query.refetch()}>Try again</Button>}
          />
        </SettingsCard>
      </SettingsPageFrame>
    );
  }

  const { plan, usage, invoices, openPlanChangeRequest, billing } = query.data;
  const isCashfree = billing.provider === "cashfree";
  const isPaypal = billing.provider === "paypal";
  const gatewayName = isPaypal ? "PayPal" : isCashfree ? "Cashfree" : "Razorpay";
  const billingCurrency = billing.currency ?? "INR";
  const isUsd = billingCurrency === "USD";
  const availablePlans = availablePlansQuery.data ?? [];

  function handleOpenRequestDialog() {
    const defaultTarget = availablePlans.find((p) => p.code !== plan?.code) ?? availablePlans[0];
    if (defaultTarget) {
      setSelectedPlanId(defaultTarget.id);
    }
    setSelectedInterval(plan?.interval ?? "monthly");
    setRequestNote("");
    setRequestDialogOpen(true);
  }

  function handleSubmitRequest(e: FormEvent) {
    e.preventDefault();
    if (!selectedPlanId) {
      toast.error("Please select a target plan.");
      return;
    }

    requestMutation.mutate(
      {
        toPlanId: selectedPlanId,
        interval: selectedInterval,
        note: requestNote.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          toast.success(data.message || "Plan change request submitted.");
          setRequestDialogOpen(false);
          refresh();
        },
        onError: (err) => {
          toast.error(errorMessage(err));
        },
      },
    );
  }

  function handleCancelRequest() {
    if (!openPlanChangeRequest) return;
    cancelMutation.mutate(
      { id: openPlanChangeRequest.id },
      {
        onSuccess: () => {
          toast.success("Plan change request cancelled.");
          setConfirmCancelOpen(false);
          refresh();
        },
        onError: (err) => {
          toast.error(errorMessage(err));
        },
      },
    );
  }

  return (
    <SettingsPageFrame title={TITLE} description={DESCRIPTION}>
      {/* Open Plan Change Request Banner */}
      {openPlanChangeRequest ? (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-foreground">
                Plan change request pending review ({openPlanChangeRequest.toPlanName} · {openPlanChangeRequest.interval})
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Requested on {new Date(openPlanChangeRequest.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}. Our team is processing your request.
                {openPlanChangeRequest.note ? ` Note: "${openPlanChangeRequest.note}"` : null}
              </p>
            </div>
          </div>
          {isOwner ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmCancelOpen(true)}
              disabled={cancelMutation.isPending}
            >
              Cancel request
            </Button>
          ) : null}
        </div>
      ) : null}

      <Tabs defaultValue="plan" className="w-full">
        <TabsList>
          <TabsTrigger value="plan">Plan & usage</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
        </TabsList>

        {/* Tab 1: Plan & Usage */}
        <TabsContent value="plan" className="space-y-6 pt-4">
          <SettingsCard
            title="Current plan"
            description="Active subscription tier and billing schedule"
            actions={
              isOwner && !openPlanChangeRequest ? (
                <Button onClick={handleOpenRequestDialog} size="sm">
                  Request plan change
                </Button>
              ) : undefined
            }
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border p-4 bg-card">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">{plan?.name ?? "Custom Plan"}</h3>
                  <Badge variant={plan?.status === "active" ? "default" : "secondary"}>
                    {plan?.status === "trialing" ? `Trial (${plan.daysLeftInTrial ?? 0} days left)` : plan?.status ?? "Active"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Billed {plan?.interval ?? "monthly"}
                  {plan?.currentPeriodEnd
                    ? ` · Renews ${new Date(plan.currentPeriodEnd).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
                    : null}
                </p>

                {!isOwner ? (
                  <p className="text-xs text-muted-foreground mt-4 italic">
                    Note: Only the store owner can submit plan change requests.
                  </p>
                ) : null}
              </div>

              <div className="rounded-lg border border-border p-4 bg-muted/20">
                <h4 className="text-xs font-semibold text-foreground mb-2">What your plan includes</h4>
                <ul className="text-xs text-muted-foreground space-y-1.5">
                  {(availablePlans.find((p) => p.code === plan?.code)?.bullets ?? []).map((line) => (
                    <li key={line} className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> {line}
                    </li>
                  ))}
                </ul>
                <Button variant="link" size="sm" className="mt-2 h-auto p-0 text-xs" onClick={() => setCompareOpen(true)}>
                  Compare all features
                </Button>
              </div>
            </div>
          </SettingsCard>

          {billing.available && isOwner ? (
            <SettingsCard
              title="Pay for your plan"
              description="Pay by card or UPI AutoPay. Your free trial or free period is never cut short: the first charge happens when it ends."
            >
              {billing.testMode ? (
                <p className="mb-3 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                  Payments are in test mode right now: use {gatewayName} test payment details, no real money moves.
                </p>
              ) : null}
              {billing.hasSubscription && billing.authorized && plan?.status !== "active" && plan?.status !== "cancelled" ? (
                <div className="space-y-3">
                  <p className="text-sm text-foreground">
                    Automatic payment is set up through {gatewayName}.{" "}
                    {billing.firstChargeAt
                      ? `Your free trial runs until ${new Date(billing.firstChargeAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}; the first charge happens then, and you keep full access until it does.`
                      : billing.activationRequired
                        ? "Your first payment has not come through yet. It is taken on the payment provider's schedule, usually within a day, and this page updates when it arrives. If you would rather pay now, stop automatic payment and choose your plan again."
                        : "Your first charge happens when your free period ends."}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setConfirmCancelSubOpen(true)}>
                    Stop automatic payment
                  </Button>
                </div>
              ) : billing.hasSubscription && plan?.status === "active" ? (
                <div className="space-y-3">
                  <p className="text-sm text-foreground">
                    {billing.cancelAt
                      ? `Your subscription ends on ${new Date(billing.cancelAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}. You keep your plan until then.`
                      : "Your subscription renews automatically."}
                  </p>
                  {!billing.cancelAt ? (
                    <Button variant="outline" size="sm" onClick={() => setConfirmCancelSubOpen(true)}>
                      Cancel subscription
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="flex flex-wrap items-end gap-3">
                  {billing.activationRequired ? (
                    <p className="w-full rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
                      Your store is not on an active plan yet. Choose a plan below and pay to activate it and unlock everything it includes.
                    </p>
                  ) : null}
                  {billing.hasSubscription && !billing.authorized ? (
                    <p className="w-full text-xs text-muted-foreground">
                      Payment set-up was started but not finished. Choose your plan and continue to try again.
                    </p>
                  ) : null}
                  <div className="min-w-48 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Plan</p>
                    <SimpleSelect
                      value={payPlanCode}
                      onChange={setPayPlanCode}
                      placeholder="Choose a plan"
                      options={availablePlans.map((p) => {
                        const amount = isUsd
                          ? (payInterval === "yearly" ? p.priceYearlyUsdCents : p.priceMonthlyUsdCents) ?? 0
                          : (payInterval === "yearly" ? p.priceYearlyPaise : p.priceMonthlyPaise);
                        return {
                          value: p.code,
                          label: `${p.name} (${formatMoney(amount, billingCurrency)}/${payInterval === "yearly" ? "yr" : "mo"})`,
                        };
                      })}
                    />
                  </div>
                  <div className="min-w-40 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Billed</p>
                    <SimpleSelect
                      value={payInterval}
                      onChange={(v) => setPayInterval(v === "yearly" ? "yearly" : "monthly")}
                      options={[
                        { value: "monthly", label: "Monthly" },
                        { value: "yearly", label: "Yearly" },
                      ]}
                    />
                  </div>
                  {isCashfree && !billing.phoneOnFile ? (
                    <div className="min-w-48 space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Mobile number</p>
                      <Input
                        value={payPhone}
                        onChange={(e) => setPayPhone(e.target.value)}
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="10-digit mobile"
                        aria-label="Mobile number for the payment mandate"
                      />
                    </div>
                  ) : null}
                  <Button
                    size="sm"
                    disabled={!payPlanCode || subscribeMutation.isPending || (isCashfree && !billing.phoneOnFile && payPhone.replace(/\D/g, "").length < 10)}
                    onClick={() =>
                      subscribeMutation.mutate(
                        {
                          planCode: payPlanCode,
                          interval: payInterval,
                          ...(isCashfree ? { ...(billing.phoneOnFile ? {} : { phone: payPhone.trim() }), returnUrl: `${window.location.origin}${window.location.pathname}` } : {}),
                        },
                        {
                          onSuccess: (res) => {
                            if (res.checkoutSession) {
                              void openCashfreeCheckout(res.checkoutSession).catch((e: unknown) => {
                                // errorMessage hides plain Errors; this one is about Cashfree's own script, so show it.
                                console.error("Cashfree checkout failed", e);
                                toast.error(e instanceof Error && e.message ? e.message : errorMessage(e));
                              });
                            } else if (res.checkoutUrl) window.location.assign(res.checkoutUrl);
                            else toast.error(`${gatewayName} did not return a payment page. Try again.`);
                          },
                          onError: (e) => toast.error(errorMessage(e)),
                        },
                      )
                    }
                  >
                    {subscribeMutation.isPending ? "Opening payment page..." : "Continue to payment"}
                  </Button>
                </div>
              )}
            </SettingsCard>
          ) : null}

          <SettingsCard
            title="Usage & quotas"
            description="Active resource utilization for this store"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              {usage.map((item) => (
                <div key={item.key} className="rounded-lg border border-border p-4 bg-card">
                  <p className="text-xs font-medium text-muted-foreground">{item.description}</p>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-2xl font-semibold text-foreground">{item.used}</span>
                    <span className="text-xs text-muted-foreground">
                      / {item.limit != null ? `${item.limit} ${item.unit}` : "Unlimited"}
                    </span>
                  </div>
                  {item.limit != null ? (
                    <div className="mt-2 w-full bg-muted rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-full ${item.percentUsed > 90 ? "bg-destructive" : item.percentUsed > 75 ? "bg-amber-500" : "bg-primary"}`}
                        style={{ width: `${Math.min(item.percentUsed, 100)}%` }}
                      />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </SettingsCard>
        </TabsContent>

        {/* Tab 2: Invoices */}
        <TabsContent value="invoices" className="pt-4">
          <SettingsCard
            title="Platform billing invoices"
            description="History of subscription charges and platform fees for your store"
          >
            {invoices.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                No invoices issued yet.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-border bg-muted/40 font-medium text-muted-foreground">
                    <tr>
                      <th className="p-3">Invoice #</th>
                      <th className="p-3">Issued date</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right">Invoice</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-muted/20">
                        <td className="p-3 font-medium text-foreground">{inv.number}</td>
                        <td className="p-3 text-muted-foreground">
                          {new Date(inv.issuedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </td>
                        <td className="p-3 font-medium text-foreground">
                          {formatMoney(inv.amountPaise, inv.currency ?? billingCurrency)}
                        </td>
                        <td className="p-3">
                          <Badge variant={inv.status === "paid" ? "outline" : "secondary"}>
                            {inv.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!inv.downloadable}
                            onClick={() => downloadInvoicePdf(inv.id, inv.number).catch((e) => toast.error(errorMessage(e)))}
                          >
                            Download PDF
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SettingsCard>
        </TabsContent>
      </Tabs>

      {/* Request Plan Change Modal Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request plan change</DialogTitle>
            <DialogDescription>
              Select a target plan and billing interval. Our platform team will review your request.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitRequest} className="grid gap-4 py-2">
            <Field id="targetPlan" label="Target plan">
              <SimpleSelect
                id="targetPlan"
                value={selectedPlanId}
                placeholder="Select a plan"
                onChange={setSelectedPlanId}
                options={availablePlans.map((p) => {
                  const amount = isUsd ? p.priceMonthlyUsdCents ?? 0 : p.priceMonthlyPaise;
                  return {
                    value: p.id,
                    label: `${p.name} (${formatMoney(amount, billingCurrency)}/mo)`,
                  };
                })}
              />
            </Field>

            <Field id="targetInterval" label="Billing interval">
              <SimpleSelect
                id="targetInterval"
                value={selectedInterval}
                placeholder="Select interval"
                onChange={(val) => setSelectedInterval(val as "monthly" | "yearly")}
                options={[
                  { value: "monthly", label: "Monthly billing" },
                  { value: "yearly", label: "Yearly billing" },
                ]}
              />
            </Field>

            <Field id="requestNote" label="Note (optional)" hint="Any specific requests or requirements.">
              <Input
                id="requestNote"
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                placeholder="e.g. Higher catalog limit needed for festive season"
              />
            </Field>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRequestDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={requestMutation.isPending || !selectedPlanId}>
                {requestMutation.isPending ? "Submitting…" : "Submit request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Cancel Dialog */}
      <ConfirmDialog
        open={confirmCancelSubOpen}
        onOpenChange={setConfirmCancelSubOpen}
        title="Cancel your subscription?"
        description="You keep your plan until the end of the period you have paid for. After that the store moves to the restricted free tier."
        confirmLabel="Cancel subscription"
        destructive
        pending={cancelSubscriptionMutation.isPending}
        onConfirm={() =>
          cancelSubscriptionMutation.mutate(undefined, {
            onSuccess: () => {
              toast.success("Subscription cancelled");
              setConfirmCancelSubOpen(false);
              refresh();
            },
            onError: (e) => toast.error(errorMessage(e)),
          })
        }
      />

      <ConfirmDialog
        open={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
        title="Cancel plan change request?"
        description="Are you sure you want to withdraw this plan change request?"
        confirmLabel="Yes, cancel request"
        cancelLabel="Keep request"
        destructive
        pending={cancelMutation.isPending}
        onConfirm={handleCancelRequest}
      />
    
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Compare all features</DialogTitle>
            <DialogDescription>What each plan includes.</DialogDescription>
          </DialogHeader>
          {compareQuery.data ? (
            <table className="w-full text-left text-xs">
              <thead>
                <tr>
                  <th className="py-1 pr-3" />
                  {compareQuery.data.plans.map((p) => (
                    <th key={p.code} className="px-2 py-1 font-semibold">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              {compareQuery.data.groups.map((g) => (
                <tbody key={g.groupName}>
                  <tr>
                    <td colSpan={compareQuery.data.plans.length + 1} className="pb-1 pt-4 text-sm font-semibold">
                      {g.groupName}
                    </td>
                  </tr>
                  {g.rows.map((r) => (
                    <tr key={r.label} className="border-t border-border/60">
                      <td className="py-1.5 pr-3" title={r.tooltip ?? undefined}>
                        {r.label}
                      </td>
                      {compareQuery.data.plans.map((p) => {
                        const c = r.cells[p.code];
                        return (
                          <td key={p.code} className="px-2 py-1.5">
                            {!c || c.k === "none" ? "-" : c.k === "tick" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-label="Included" /> : c.k === "soon" ? <span className="text-muted-foreground">Soon</span> : c.t}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          ) : (
            <p className="text-xs text-muted-foreground">{compareQuery.isError ? "The comparison could not be loaded." : "Loading..."}</p>
          )}
        </DialogContent>
      </Dialog>
</SettingsPageFrame>
  );
}
